// lib/api.ts

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://coursyflix-backend.onrender.com/api";

// --------------------- TOKEN ---------------------
export const getToken = (): string | null => {
  if (typeof window === "undefined") return null;

  // ✅ Support both token keys
  return (
    localStorage.getItem("adminToken") ||
    localStorage.getItem("token")
  );
};

// --------------------- LOGOUT ---------------------
const logoutUser = () => {
  if (typeof window === "undefined") return;

  // ✅ Clear all possible auth tokens
  localStorage.removeItem("token");
  localStorage.removeItem("adminToken");
};

// --------------------- ERROR HANDLING ---------------------
export const handleError = async (response: Response) => {
  let data: any;

  try {
    data = await response.json();
  } catch {
    data = {
      message: response.statusText || "Server error",
    };
  }

  console.error(`API Error ${response.status}:`, data);

  // ✅ Auto logout on unauthorized
  if (response.status === 401) {
    logoutUser();

    throw new Error("Session expired. Redirecting to login...");
  }

  throw new Error(
    data?.message ||
      data?.error ||
      `Request failed (${response.status})`
  );
};

// --------------------- API REQUEST WRAPPER ---------------------
export async function apiRequest(
  endpoint: string,
  options: RequestInit = {}
) {
  const token = getToken();

  const url = `${API_BASE}${
    endpoint.startsWith("/") ? endpoint : `/${endpoint}`
  }`;

  // ✅ Detect FormData uploads
  const isFormData = options.body instanceof FormData;

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // ✅ Only set JSON header if NOT FormData
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  // ✅ Attach auth token
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else {
    console.warn("⚠️ No token found. Request may fail:", url);
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // ✅ Handle failed requests
  if (!response.ok) {
    await handleError(response);
  }

  // ✅ Safely parse response
  const text = await response.text();

  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return text;
  }
}

// --------------------- COURSE APIs ---------------------
export const courseAPI = {
  getAll: () => apiRequest('/courses'),
  getById: (id: string) => apiRequest(`/courses/${id}`),
  // ✅ ADD THIS
  getCourseSections: (courseId: string) =>
    apiRequest(`/courses/${courseId}/sections`),
  create: (data: any) =>
    apiRequest('/courses', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) =>
    apiRequest(`/courses/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => apiRequest(`/courses/${id}`, { method: 'DELETE' }),
  publish: (id: string, status: 'PUBLISHED' | 'DRAFT') =>
    apiRequest(`/courses/${id}/publish`, { method: 'PUT', body: JSON.stringify({ status }) }),
  lock: (id: string, isLocked: boolean, lockExpiry?: string) =>
    apiRequest(`/courses/${id}/lock`, { method: 'PUT', body: JSON.stringify({ isLocked, lockExpiry }) }),
  getStats: () => apiRequest('/courses/stats'),
};

export const sectionAPI = {
  create: (courseId: string, data: { title: string }) =>
    apiRequest(`/courses/${courseId}/sections`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // ✅ FIXED
  update: (sectionId: string, data: { title: string }) =>
    apiRequest(`/courses/sections/${sectionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // ✅ FIXED
  delete: (sectionId: string) =>
    apiRequest(`/courses/sections/${sectionId}`, {
      method: 'DELETE',
    }),

  reorder: (courseId: string, sectionIds: string[]) =>
    apiRequest(`/courses/${courseId}/sections/reorder`, {
      method: 'POST',
      body: JSON.stringify({ sectionIds }),
    }),
};

export const chapterAPI = {
  // ✅ FIXED
  create: (sectionId: string, data: any) =>
    apiRequest(`/courses/sections/${sectionId}/chapters`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // ✅ FIXED
  update: (chapterId: string, data: any) =>
    apiRequest(`/courses/chapters/${chapterId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // ✅ FIXED
  delete: (chapterId: string) =>
    apiRequest(`/courses/chapters/${chapterId}`, {
      method: 'DELETE',
    }),
};
export const mediaAPI = {
  add: (chapterId: string, data: any) => {
    const formData = new FormData();

    // 📁 file upload
    if (data.file) {
      formData.append("file", data.file);
    }

    // 🧠 normal fields
    if (data.type) formData.append("type", data.type);
    if (data.url) formData.append("url", data.url);
    if (data.name) formData.append("name", data.name);

    // 📦 metadata (must stringify)
    if (data.metadata) {
      formData.append("metadata", JSON.stringify(data.metadata));
    }

    return apiRequest(`/chapters/${chapterId}/media`, {
      method: "POST",
      body: formData,
    });
  },

  delete: (mediaId: string) =>
    apiRequest(`/media/${mediaId}`, { method: "DELETE" }),
};
export const questionAPI = {
  create: (chapterId: string, data: any) =>
    apiRequest(`/courses/chapters/${chapterId}/questions`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (questionId: string, data: any) =>
    apiRequest(`/courses/questions/${questionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (questionId: string) =>
    apiRequest(`/courses/questions/${questionId}`, {
      method: 'DELETE',
    }),
};
export const answerAPI = {
  add: (questionId: string, data: any) =>
    apiRequest(`/courses/questions/${questionId}/answers`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (answerId: string, data: any) =>
    apiRequest(`/courses/answers/${answerId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (answerId: string) =>
    apiRequest(`/courses/answers/${answerId}`, {
      method: 'DELETE',
    }),
};


// lib/api.ts  (or wherever your API objects are defined)

export const announcementAPI = {
  getAnnouncements: async (): Promise<any> => {
    return apiRequest('/announcements');
  },

  createAnnouncement: async (data: {
    title: string;
    message: string;
    target: 'ALL' | 'SPECIFIC_COURSE';
    targetCourseId?: string;
  }): Promise<any> => {
    return apiRequest('/announcements', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
  },

  updateAnnouncement: async (
    id: string,
    data: {
      title?: string;
      message?: string;
      target?: 'ALL' | 'SPECIFIC_COURSE';
      targetCourseId?: string;
    }
  ): Promise<any> => {
    return apiRequest(`/announcements/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
  },

  deleteAnnouncement: async (id: string): Promise<any> => {
    return apiRequest(`/announcements/${id}`, {
      method: 'DELETE',
    });
  },
};

export const adminChallengeAPI = {
  // Get all challenges (for admin)
  getChallenges: async (): Promise<any> => {
    return apiRequest('/challenges', { method: 'GET' });
  },

  // Get single challenge (optional but useful)
  getChallenge: async (id: string): Promise<any> => {
    return apiRequest(`/challenges/${id}`, { method: 'GET' });
  },

  // Create new challenge
  createChallenge: async (data: {
    title: string;
    description: string;
    deadline: string;
    rewards?: string;
  }): Promise<any> => {
    return apiRequest('/challenges', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
  },

  // Update existing challenge
  updateChallenge: async (id: string, data: {
    title?: string;
    description?: string;
    deadline?: string;
    rewards?: string;
    status?: 'ACTIVE' | 'CLOSED' | 'ARCHIVED';
  }): Promise<any> => {
    return apiRequest(`/challenges/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
  },

  // Delete challenge
  deleteChallenge: async (id: string): Promise<any> => {
    return apiRequest(`/challenges/${id}`, {
      method: 'DELETE',
    });
  },

  // ✅ NEW: Get all submissions for a specific challenge (Very Important for Admin)
  getChallengeSubmissions: async (challengeId: string): Promise<any> => {
    return apiRequest(`/challenges/${challengeId}/submissions`, {
      method: 'GET',
    });
  },

  // Optional: Get all submissions across all challenges
  getAllSubmissions: async (): Promise<any> => {
    return apiRequest('/challenges/submissions', { method: 'GET' });
  },
};
